import type { FormGroup } from '@angular/forms';
import type { ReactiveFormRequestHandler } from '../../adapters/reactive-form-builder.adapters';
import {
  createReactiveFormRequestAdapterState,
  isReactiveFormRequestId,
  type ReactiveFormRequestAdapter,
  type ReactiveFormRequestAdapterState,
  type ReactiveFormRequestOperation,
} from '../../adapters/reactive-form-request.adapter';
import type { Form } from '../../interfaces/form.interface';
import { FormType } from '../../interfaces/form.interface';
import type { ReactiveFormPayloadSerializerResolvers } from '../../interfaces/reactive-form-serializer.interface';
import { ReactiveFormPayloadSerializer } from '../form-submission.utils';
import { ReactiveFormSubmitAdapterExecutor } from '../submit/reactive-form-submit-adapter-executor';
import { ReactiveFormSubmitAfterSendCoordinator } from '../submit/reactive-form-submit-after-send-coordinator';
import { ReactiveFormSubmitCompletionPolicy } from '../submit/reactive-form-submit-completion-policy';
import { ReactiveFormSubmitDecision } from '../submit/reactive-form-submit-decision';
import { ReactiveFormSubmitErrorPolicy } from '../submit/reactive-form-submit-error-policy';
import { ReactiveFormSubmitRefreshPolicy } from '../submit/reactive-form-submit-refresh-policy';
import { ReactiveFormSubmitRequestExecutor } from '../submit/reactive-form-submit-request-executor';
import {
  ReactiveFormSubmitResult,
  type ReactiveFormSubmitResultSnapshot,
} from '../submit/reactive-form-submit-result';
import { ReactiveFormSubmitState } from '../submit/reactive-form-submit-state';
import { ReactiveFormSubmitValidationPresenter } from '../submit/reactive-form-submit-validation-presenter';

export interface ReactiveFormSubmitFacadeConfiguration<T> {
  readonly getForm: () => Form<T> | null;
  readonly getModel: () => FormGroup | null;
  readonly getRecordHasAssociatedData: () => boolean;
  readonly getLockOnSuccess: () => boolean;
  readonly getRequest: () => ReactiveFormRequestHandler<any> | null;
  readonly getGetRequest: () => ReactiveFormRequestHandler<any> | null;
  readonly getRequestAdapter?: () => ReactiveFormRequestAdapter<T, T> | null;
  readonly getGetRequestAdapter?: () => ReactiveFormRequestAdapter<unknown, unknown> | null;
  readonly getRecordId?: () => string | number | null;
  readonly isDemoUser: () => boolean;
  readonly emitGoBack: () => void;
  readonly emitAfterSend: (response: T) => void;
  readonly getSerializerResolvers?: () => ReactiveFormPayloadSerializerResolvers;
  readonly scheduleRequestCleanup?: (cleanup: () => void) => void;
}

type SubmitButtonLabels = readonly [string, string, string];

const REQUEST_CLEANUP_DELAY_MS = 1000;

/**
 * Owns submit-button state, payload serialization, validation summaries and request lifecycle side effects.
 */
export class ReactiveFormSubmitFacade<T> {
  public validationError: string | null = null;
  public validationSummary: string | null = null;
  public warning: string | null = null;

  private readonly requestErrorMessages: string[] = [];
  private readonly requestAdapterState =
    createReactiveFormRequestAdapterState();
  private readonly getRequestAdapterState =
    createReactiveFormRequestAdapterState();
  private readonly requestExecutor = new ReactiveFormSubmitRequestExecutor<T, T>();
  private readonly adapterExecutor = new ReactiveFormSubmitAdapterExecutor<T, T>();
  private readonly refreshPolicy = new ReactiveFormSubmitRefreshPolicy();
  private readonly completionPolicy = new ReactiveFormSubmitCompletionPolicy();
  private readonly afterSendCoordinator = new ReactiveFormSubmitAfterSendCoordinator<T>();
  private readonly errorPolicy = new ReactiveFormSubmitErrorPolicy();
  private readonly validationPresenter =
    new ReactiveFormSubmitValidationPresenter<T>();
  private readonly submitState = new ReactiveFormSubmitState();
  private lastSubmitResult = ReactiveFormSubmitResult.skipped(
    'Submit has not started yet.',
  );

  constructor(
    private readonly configuration: ReactiveFormSubmitFacadeConfiguration<T>,
  ) {}

  public get requestError(): readonly string[] {
    return this.requestErrorMessages;
  }

  public get submitResult(): ReactiveFormSubmitResultSnapshot {
    return this.lastSubmitResult;
  }

  public get buttonLabel(): string {
    const [defaultLabel, loadingLabel, doneLabel] =
      this.buttonLabels[this.currentFormType] ??
      this.buttonLabels[FormType.CREATION];
    if (this.warning) {
      return this.warning;
    }

    if (this.hasRequestError) {
      return this.hasClientExistsError
        ? this.errorPolicy.duplicateTitle
        : (this.requestErrorMessages[0] ?? '');
    }

    if (!this.hasSubmittedRequest) {
      return defaultLabel;
    }

    if (this.isRequestLoading) {
      return loadingLabel;
    }

    if (this.hasSuccessfulRequest) {
      return doneLabel;
    }

    return defaultLabel;
  }

  public get buttonClasses(): Record<string, boolean> {
    return {
      'btn-danger': Boolean(
        this.validationError ||
        (!this.warning &&
          this.currentFormType === FormType.DELETION &&
          !this.isRequestLoading &&
          !this.hasSubmittedRequest),
      ),
      'btn-warning': Boolean(
        this.warning || this.hasRequestError || this.hasClientExistsError,
      ),
      'btn-primary': Boolean(
        !this.validationError &&
        !this.warning &&
        this.currentFormType !== FormType.DELETION &&
        !this.isRequestLoading &&
        !this.hasSubmittedRequest,
      ),
      'btn-success': Boolean(
        !this.validationError && !this.warning && this.hasSuccessfulRequest,
      ),
      'btn-secondary': Boolean(
        this.hasSubmittedRequest && this.isRequestLoading,
      ),
    };
  }

  public get iconClasses(): Record<string, boolean> {
    return {
      'fa-exclamation-triangle': Boolean(
        this.validationError ||
        this.warning ||
        this.hasRequestError ||
        this.hasClientExistsError,
      ),
      'fa-save': Boolean(
        !this.hasSuccessfulRequest &&
        (this.currentFormType === FormType.DUPLICATION ||
          this.currentFormType === FormType.EDITION ||
          (this.currentFormType === FormType.CREATION &&
            !this.validationError &&
            !this.warning)),
      ),
      'fa-pencil': Boolean(
        !this.hasSuccessfulRequest && this.currentFormType === FormType.EDITION,
      ),
      'fa-trash': Boolean(
        !this.hasSuccessfulRequest &&
        !this.warning &&
        this.currentFormType === FormType.DELETION,
      ),
      'fa-check': Boolean(
        !this.validationError && !this.warning && this.hasSuccessfulRequest,
      ),
      'fa-spinner fa-spin': Boolean(
        this.hasSubmittedRequest && this.isRequestLoading,
      ),
    };
  }

  public get isButtonDisabled(): boolean {
    return (
      this.isRequestLoading ||
      (this.hasSuccessfulRequest && this.configuration.getLockOnSuccess())
    );
  }

  public get showRepeatActionLabel(): boolean {
    return (
      !this.configuration.getLockOnSuccess() &&
      this.hasSuccessfulRequest &&
      (this.currentFormType === FormType.DUPLICATION ||
        this.currentFormType === FormType.CREATION)
    );
  }

  public get repeatActionLabel(): string {
    return this.currentFormType === FormType.DUPLICATION
      ? 'Duplicar otro'
      : 'Crear otro';
  }

  public initialize(): void {
    const isDemo = this.configuration.isDemoUser();

    this.requestErrorMessages[0] = isDemo
      ? 'Operación no permitida'
      : 'No hemos podido completar la operación. Revisa los datos e inténtalo de nuevo.';
    this.requestErrorMessages[1] = isDemo ? '' : 'Intentar de nuevo';
    this.validationPresenter.rebuild(
      this.configuration.getForm(),
      this.configuration.getModel(),
    );
  }

  /**
   * Evaluates the form and sends the request when the payload is valid.
   */
  public submit(): void {
    const form = this.configuration.getForm();
    const model = this.configuration.getModel();

    if (!form || !model) {
      this.lastSubmitResult = ReactiveFormSubmitResult.skipped(
        'Form or model is not ready.',
      );
      ReactiveFormSubmitDecision.resolve({
        formType: this.currentFormType,
        hasForm: Boolean(form),
        hasModel: Boolean(model),
        hasInvalidSubmittableControls: false,
        recordHasAssociatedData: false,
        hasWarning: Boolean(this.warning),
        requiresExistingRecordId: false,
        hasExistingRecordId: true,
      });
      return;
    }

    this.validationError = null;
    this.validationSummary = null;

    this.validationPresenter.refreshControlValidity(model);

    const invalidSubmittableControls =
      this.validationPresenter.hasInvalidSubmittableControls(form, model);
    const prePayloadDecision = ReactiveFormSubmitDecision.resolve({
      formType: this.currentFormType,
      hasForm: true,
      hasModel: true,
      hasInvalidSubmittableControls: invalidSubmittableControls,
      recordHasAssociatedData: this.configuration.getRecordHasAssociatedData(),
      hasWarning: Boolean(this.warning),
      requiresExistingRecordId: false,
      hasExistingRecordId: true,
    });

    if (prePayloadDecision.action === 'showValidationErrors') {
      const validationPresentation = this.validationPresenter.present(form, model);

      this.lastSubmitResult = ReactiveFormSubmitResult.validationError(
        'Visible submittable controls are invalid.',
      );
      this.validationError = validationPresentation.title;
      this.validationSummary = validationPresentation.summary;
      return;
    }

    if (prePayloadDecision.action === 'showDeletionWarning') {
      this.lastSubmitResult = ReactiveFormSubmitResult.warning(
        'Deletion requires associated-data confirmation.',
      );
      this.warning =
        'El registro tiene datos, por lo que se borrará toda la información asociada. <br> ¿Continuar?';
      return;
    }

    this.warning = null;
    this.submitState.markSubmitting();
    this.lastSubmitResult = ReactiveFormSubmitResult.submitted();

    const payload = ReactiveFormPayloadSerializer.serialize(
      form,
      model.getRawValue(),
      this.configuration.getSerializerResolvers?.() ?? {},
    );

    if (this.currentFormType === FormType.DUPLICATION) {
      delete payload['id'];
    }

    this.ensureExistingRecordId(payload);

    const payloadDecision = ReactiveFormSubmitDecision.resolve({
      formType: this.currentFormType,
      hasForm: true,
      hasModel: true,
      hasInvalidSubmittableControls: false,
      recordHasAssociatedData: false,
      hasWarning: false,
      requiresExistingRecordId: this.requiresExistingRecordId(),
      hasExistingRecordId: this.hasSerializableId(payload['id']),
    });

    if (payloadDecision.action === 'missingRecordId') {
      this.submitState.reset();
      this.lastSubmitResult = ReactiveFormSubmitResult.validationError(
        'Existing record id is missing.',
      );
      this.validationError = 'No se ha podido identificar el registro.';
      this.validationSummary =
        'No hemos podido completar la operación porque falta el identificador interno del registro.';
      return;
    }

    this.sendRequest(payload as T);
  }

  private get currentFormType(): FormType {
    return this.configuration.getForm()?.type ?? FormType.CREATION;
  }

  private get hasSubmittedRequest(): boolean {
    return this.submitState.hasSubmittedRequest;
  }

  private get hasCompletedRequest(): boolean {
    return this.submitState.hasCompletedRequest;
  }

  private get isRequestLoading(): boolean {
    const requestState = this.currentRequestState;

    if (requestState) {
      return requestState.isLoading;
    }

    return this.configuration.getRequest()?.isLoading === true;
  }

  private get hasRequestError(): boolean {
    const requestState = this.currentRequestState;

    if (requestState) {
      return this.hasSubmittedRequest && requestState.hasError;
    }

    return (
      this.hasSubmittedRequest &&
      this.configuration.getRequest()?.hasError === true
    );
  }

  private get hasClientExistsError(): boolean {
    const requestState = this.currentRequestState;

    if (requestState) {
      return this.hasSubmittedRequest && requestState.hasErrorCliExists;
    }

    return (
      this.hasSubmittedRequest &&
      this.configuration.getRequest()?.hasErrorCliExists === true
    );
  }

  private get hasSuccessfulRequest(): boolean {
    const requestState = this.currentRequestState;

    if (requestState) {
      return (
        this.hasSubmittedRequest &&
        this.hasCompletedRequest &&
        !requestState.hasError
      );
    }

    return (
      this.hasSubmittedRequest &&
      this.hasCompletedRequest &&
      this.configuration.getRequest()?.hasError === false
    );
  }

  private get currentRequestState(): ReactiveFormRequestAdapterState | null {
    return this.configuration.getRequestAdapter?.()
      ? this.requestAdapterState
      : null;
  }

  private get buttonLabels(): Record<FormType, SubmitButtonLabels> {
    return {
      [FormType.CREATION]: ['Guardar y continuar', 'Guardando...', 'Guardado'],
      [FormType.EDITION]: [
        'Guardar cambios',
        'Guardando cambios...',
        'Cambios guardados',
      ],
      [FormType.DUPLICATION]: ['Duplicar', 'Duplicando...', 'Duplicado'],
      [FormType.DELETION]: [
        'Borrar registro',
        'Borrando registro...',
        'Registro borrado',
      ],
      [FormType.INFORMATIVE]: ['', '', ''],
      [FormType.EXPORT]: ['', '', ''],
      [FormType.GENERATION]: [
        'Generar informe',
        'Generando informe...',
        'Informe generado',
      ],
    };
  }

  private ensureExistingRecordId(payload: Record<string, unknown>): void {
    if (!this.requiresExistingRecordId()) {
      return;
    }

    const recordId = this.configuration.getRecordId?.();

    if (this.hasSerializableId(recordId)) {
      payload['id'] = recordId;
      return;
    }

    if (
      this.isPlatformUserForm() &&
      !this.hasSerializableId(payload['id']) &&
      this.hasSerializableId(payload['id_plataforma'])
    ) {
      payload['id'] = payload['id_plataforma'];
    }
  }

  private requiresExistingRecordId(): boolean {
    return (
      this.currentFormType === FormType.EDITION ||
      this.currentFormType === FormType.DELETION
    );
  }

  private hasSerializableId(value: unknown): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private isPlatformUserForm(): boolean {
    const formName = String(this.configuration.getForm()?.name ?? '')
      .trim()
      .toLowerCase();

    return formName === 'usuarios' || formName === 'usuarios-summary';
  }

  private sendRequest(payload: T): void {
    const requestAdapter = this.configuration.getRequestAdapter?.() ?? null;

    if (requestAdapter) {
      void this.sendAdapterRequest(requestAdapter, payload);
      return;
    }

    const request = this.configuration.getRequest();

    if (!request) {
      return;
    }

    this.requestExecutor.execute({
      request,
      payload,
      refreshAfterSuccess: () =>
        this.configuration.getGetRequest()?.safePerform(),
      onSuccess: (response) => this.completeSuccessfulSubmit(response),
      onFailure: (error) => this.handleRequestFailure(error),
      scheduleCleanup: (cleanup) => this.scheduleRequestCleanup(cleanup),
    });
  }

  private async sendAdapterRequest(
    requestAdapter: ReactiveFormRequestAdapter<T, T>,
    payload: T,
  ): Promise<void> {
    try {
      const response = await this.adapterExecutor.execute({
        adapter: requestAdapter,
        operation: this.getAdapterOperation(),
        payload,
        id: this.resolveRecordId(payload),
        state: this.requestAdapterState,
      });

      await this.refreshAfterSuccessfulAdapterRequest();
      this.completeSuccessfulSubmit(response);
    } catch (error) {
      this.handleRequestFailure(error);
    }
  }

  private completeSuccessfulSubmit(response: T): void {
    this.submitState.markCompleted();
    this.lastSubmitResult = ReactiveFormSubmitResult.completed();
    this.afterSendCoordinator.complete({
      response,
      completion: this.completionPolicy.resolve(),
      emitAfterSend: (responseToEmit) =>
        this.configuration.emitAfterSend(responseToEmit),
      emitGoBack: () => this.configuration.emitGoBack(),
    });
  }

  private handleRequestFailure(error: unknown): void {
    this.submitState.markFailed();
    this.lastSubmitResult = ReactiveFormSubmitResult.failed();

    const errorPresentation = this.errorPolicy.resolve({
      error,
      formType: this.currentFormType,
      hasClientExistsError: this.hasClientExistsError,
    });

    this.validationError = errorPresentation.title;
    this.validationSummary = errorPresentation.summary;
  }

  private getAdapterOperation(): ReactiveFormRequestOperation {
    switch (this.currentFormType) {
      case FormType.CREATION:
      case FormType.DUPLICATION:
        return 'create';
      case FormType.EDITION:
        return 'update';
      case FormType.DELETION:
        return 'delete';
      case FormType.INFORMATIVE:
      case FormType.EXPORT:
      case FormType.GENERATION:
        return 'submit';
    }
  }

  private resolveRecordId(payload: T): string | number | null {
    const configuredId = this.configuration.getRecordId?.();

    if (isReactiveFormRequestId(configuredId)) {
      return configuredId;
    }

    if (payload === null || typeof payload !== 'object') {
      return null;
    }

    const payloadId = (payload as { readonly id?: unknown }).id;

    return isReactiveFormRequestId(payloadId) ? payloadId : null;
  }

  private async refreshAfterSuccessfulAdapterRequest(): Promise<void> {
    await this.refreshPolicy.execute({
      adapter: this.configuration.getGetRequestAdapter?.() ?? null,
      adapterState: this.getRequestAdapterState,
      fallbackRequest: this.configuration.getGetRequest(),
      logError: (message, error) => console.error(message, error),
    });
  }

  private scheduleRequestCleanup(cleanup: () => void): void {
    if (this.configuration.scheduleRequestCleanup) {
      this.configuration.scheduleRequestCleanup(cleanup);
      return;
    }

    setTimeout(cleanup, REQUEST_CLEANUP_DELAY_MS);
  }
}
